import { SLIPPAGE_TOLERANCE, TRANSACTION_DEADLINE } from '@/helpers/constants';
import {
  getErc20Contract,
  getLiquidityDelegateContract,
  getPortfolioAndPairRegistryContract,
  getRouterContract,
  transactionWithEstimatedGas,
} from '@/helpers/contract.helper';
import { Portfolio } from '@/sdk/entities/portfolio';
import { applySlippageInPercents, fromWei, toWei } from '@/sdk/utils';
import { SelectedPortfolioTokens } from '@/store/modules/portfolios/models/selected-portfolio-tokens.interface';
import { fetchPricesInUSDByAddresses } from '@/helpers/backend-api';
import {
  fetchPortfolioTokensOwned,
  fetchTotalSupply,
} from '@/store/modules/portfolios/portfolio-api';
import { safeDateNowPlusEstimatedMinutes } from '@/helpers/utils';
import { Pair } from '@/sdk/entities/pair';
import { BIG_ZERO, ethersToBigNumber, toBigNumber } from '@/utils/bigNumber';
import { Token } from '@/sdk/entities/token';
import { getInstance } from '@snapshot-labs/lock/plugins/vue3';
import { getLiquidityDelegateAddress, getRouterAddress } from '@/helpers/address.helper';
import { ChainId, SOLIDITY_TYPE_MAXIMA, SolidityType } from '@/sdk/constants';
import { BOT_USD_DECIMALS } from '@/helpers/decimals-env';
import { getCrossChainPortfolioInfos } from '@/helpers/cross-chain-api';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { PortfolioCrossChain } from '@/sdk/entities/portfolio-crosschain';
import { PortfolioSource } from '@/sdk/entities/PortfolioSource';
import { ENABLE_CROSS_CHAIN } from '@/helpers/crossChainEnv.helper';
import { ethers } from 'ethers';
import { getWithdrawMethodParameters } from '@/helpers/portfolioWithdraw.helper';
import { getDepositMethodParameters } from '@/helpers/portfolioDeposit.helper';
import BigNumber from 'bignumber.js';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { PortfolioInfo } from './models/portfolio-info.interface';
import { flatPortfolioInfoAndConvertToPortfolioDto } from '@/helpers/portfolio.helper';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import { createPairsWithVirtualReserves } from './portfolio-virtual-reserves';

const CONSOLE_LABEL_GREEN = 'color: white; background-color: #95B46A';

const LOGGER = {
  groupCollapsed: (...label: any[]) => {
    if (isLoggingDisabled()) return;

    console.groupCollapsed(...label);
  },
  groupEnd: () => {
    if (isLoggingDisabled()) return;

    console.groupEnd();
  },
  log: (message?: any, ...optionalParams: any[]) => {
    if (isLoggingDisabled()) return;

    console.log(message, ...optionalParams);
  },
};

export interface PortfolioState {
  portfolio: Portfolio[];
}

export const PORTFOLIO_ACTION_TYPES = {
  INIT_PORTFOLIOS: 'initPortfolios',
  UPDATE_PORTFOLIOS_INFO: 'updatePortfoliosInfo',
  GET_PORTFOLIO_INFO: 'getPortfolioInfo',
  GET_PORTFOLIO_CROSS_CHAIN_INFO: 'getPortfolioCrossChainInfo',
  GET_ALL_PORTFOLIO_INFO: 'getAllPortfolioInfo',
  GET_VIRTUAL_RESERVES: 'getVirtualReserves',
  GET_PORTFOLIO_STATISTICS: 'getPortfolioStatistics',
  PORTFOLIO_ADD_LIQUIDITY: 'portfolioAddLiquidity',
  PORTFOLIO_WITHDRAW: 'portfolioWithdraw',
  PORTFOLIO_CHECK_ALLOWANCE: 'portfolioCheckAllowance',
  PORTFOLIO_APPROVE: 'portfolioApprove',
  SET_DEPOSIT_SETTINGS: 'setDepositSettings',
  SET_WITHDRAW_SETTINGS: 'setWithdrawSettings',
  SET_PORTFOLIOS_MODE: 'setPortfoliosMode',
};
export const PORTFOLIO_MUTATION_TYPES = {
  SET_STATE: 'setState',
  ADD_PORTFOLIO: 'addPortfolio',
};

const state = {
  isStoreReady: false,
  isStatisticLoaded: false,
  isPortfoliosUpdated: true,
  portfolios: {},
  portfoliosMode: localStorage.getItem('portfoliosMode') || 'new',
  depositSettings: {
    slippageTolerance: SLIPPAGE_TOLERANCE.toString(),
    transactionDeadline: TRANSACTION_DEADLINE.toString(),
  },
  withdrawSettings: {
    slippageTolerance: SLIPPAGE_TOLERANCE.toString(),
    transactionDeadline: TRANSACTION_DEADLINE.toString(),
  },
};
const getters = {
  getAllPairs: state => {
    const portfoliosArr: Portfolio[] = Object.values(state.portfolios) ?? [];
    const pairs: Pair[] = portfoliosArr.flatMap(portfolio => Object.values(portfolio.pairs));
    return pairs || [];
  },
  getPortfolios: state => {
    return Object.values(state.portfolios);
  },
  getPortfoliosByAddresses: state => {
    return state.portfolios;
  },
};
const actions = {
  [PORTFOLIO_ACTION_TYPES.SET_PORTFOLIOS_MODE]({ commit }, mode: 'new' | 'old') {
    localStorage.setItem('portfoliosMode', mode);
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, { portfoliosMode: mode });
  },
  async [PORTFOLIO_ACTION_TYPES.GET_ALL_PORTFOLIO_INFO]({ dispatch }) {
    LOGGER.groupCollapsed('PORTFOLIO_ACTION_TYPES.GET_ALL_PORTFOLIO_INFO');
    await dispatch(PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_INFO);
    await dispatch(PORTFOLIO_ACTION_TYPES.GET_VIRTUAL_RESERVES);
    await dispatch(PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_STATISTICS);
    LOGGER.groupEnd();
  },
  async [PORTFOLIO_ACTION_TYPES.INIT_PORTFOLIOS]({ commit, dispatch }) {
    LOGGER.log('PORTFOLIO_ACTION_TYPES.INIT_PORTFOLIOS');
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, {
      isStoreReady: false,
      isStatisticLoaded: false,
      portfolios: {},
    });
    await dispatch(PORTFOLIO_ACTION_TYPES.GET_ALL_PORTFOLIO_INFO);
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, { isStoreReady: true });
    LOGGER.log('STATE.PORTFOLIOS', state.portfolios);
  },
  async [PORTFOLIO_ACTION_TYPES.UPDATE_PORTFOLIOS_INFO]({ commit, dispatch }) {
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, {
      isPortfoliosUpdated: false,
    });
    await dispatch(PORTFOLIO_ACTION_TYPES.GET_ALL_PORTFOLIO_INFO);
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, {
      isPortfoliosUpdated: true,
    });
  },
  async [PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_INFO]({ state, commit, dispatch }) {
    LOGGER.log('%c PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_INFO ', CONSOLE_LABEL_GREEN);
    const { walletState } = useEVMWallet();
    try {
      const currentChain = walletState.network?.chainId ?? +(DEFAULT_NETWORK_ID ?? -1);
      LOGGER.log('get currentChain', currentChain);
      // TODO: when add USD decimals in env, can get USD token
      // const baseUSDToken = rootGetters[GLOBAL_GETTERS.GET_BASE_TOKEN];
      const botUSDToken = new Token(
        currentChain,
        ethers.constants.AddressZero,
        BOT_USD_DECIMALS,
        'USD',
      );
      const portfolioContract = getPortfolioAndPairRegistryContract();
      const portfoliosResult: PortfolioInfo = await portfolioContract.getPortfolios();
      LOGGER.log('portfolioResult', portfoliosResult);

      const portfolioDTOs = flatPortfolioInfoAndConvertToPortfolioDto(portfoliosResult);
      LOGGER.log('portfolio DTOs : ', portfolioDTOs);

      portfolioDTOs.forEach(info => {
        const storedPortfolio: Portfolio = state.portfolios[info.contractAddress];
        if (storedPortfolio) {
          storedPortfolio.updatePortfolioInfo(info);
          LOGGER.log(storedPortfolio.contractAddress, info);
        } else {
          const portfolio = new Portfolio(info, PortfolioSource.PORTFOLIO_LOCALE, currentChain);
          LOGGER.log('local portfolios', portfolio);
          portfolio.addPriceUSDToken(botUSDToken);

          commit(PORTFOLIO_MUTATION_TYPES.ADD_PORTFOLIO, portfolio);
        }
      });
      await dispatch(PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_CROSS_CHAIN_INFO);
      await Promise.resolve(state.portfolios);
    } catch (e) {
      console.debug(e);
      await Promise.reject(e);
    }
  },
  async [PORTFOLIO_ACTION_TYPES.GET_VIRTUAL_RESERVES]({ state }) {
    console.groupCollapsed('PORTFOLIO_ACTION_TYPES.GET_VIRTUAL_RESERVES');
    try {
      await Promise.all(
        Object.keys(state.portfolios).map(key => {
          return createPairsWithVirtualReserves(state.portfolios[key]);
        }),
      );
      // await dispatch(PORTFOLIO_ACTION_TYPES.GET_CROSS_CHAIN_VIRTUAL_RESERVES);
      console.groupEnd();
      await Promise.resolve(state.portfolios);
    } catch (e) {
      console.groupEnd();
      console.debug(e);
      await Promise.reject(e);
    }
  },
  async [PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_CROSS_CHAIN_INFO]({ commit }) {
    if (!ENABLE_CROSS_CHAIN) return;
    const { walletState } = useEVMWallet();
    const currentChain = walletState.network?.chainId ?? +(DEFAULT_NETWORK_ID ?? -1);
    // TODO: when add USD decimals in env, can get USD token
    // const baseUSDToken = rootGetters[GLOBAL_GETTERS.GET_BASE_TOKEN];
    const botUSDToken = new Token(
      currentChain,
      ethers.constants.AddressZero,
      BOT_USD_DECIMALS,
      'USD',
    );

    LOGGER.log('get currentChain', currentChain);
    const crosschainPortfoliosArray = await getCrossChainPortfolioInfos(currentChain);
    LOGGER.log('crosschain portfolios', crosschainPortfoliosArray);

    crosschainPortfoliosArray.forEach(portfolio => {
      const storedPortfolio = state.portfolios[portfolio.contractAddress] as Portfolio;
      if (storedPortfolio) {
        LOGGER.log('update crosschain', portfolio);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const preparedCHPortfolio =
          PortfolioCrossChain.createLocalPortfolioFromCrosschain(portfolio);
        storedPortfolio.updatePortfolioInfo(preparedCHPortfolio);

        // NOTE: moved from `GET_PORTFOLIO_STATISTICS`
        storedPortfolio.addTotalSupply(ethersToBigNumber(portfolio.lpTokenTotalSupply));
      } else {
        const preparedCHPortfolio =
          PortfolioCrossChain.createLocalPortfolioFromCrosschain(portfolio);
        LOGGER.log('preparedCHPortfolio', preparedCHPortfolio);
        const chPortfolio = new Portfolio(
          preparedCHPortfolio,
          PortfolioSource.PORTFOLIO_CROSSCHAIN,
          +currentChain,
        );
        chPortfolio.addPriceUSDToken(botUSDToken);
        // NOTE: moved from `GET_PORTFOLIO_STATISTICS`
        chPortfolio.addTotalSupply(ethersToBigNumber(portfolio.lpTokenTotalSupply));
        LOGGER.log('chPortfolio', chPortfolio);
        commit(PORTFOLIO_MUTATION_TYPES.ADD_PORTFOLIO, chPortfolio);
      }
    });

    //await dispatch(
    //         MODULE_NAMES.ROUTES + '/' + ROUTES_ACTION_TYPES.CALCULATE_ROUTES_FROM_IN,
    //         {
    //           currencyAmountA: tokenAmount,
    //           tokenB: state[getInverseField(notEstimatedFieldType)].token,
    //           tradeOptions,
    //         },
    //         { root: true },
    //       );
  },
  async [PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_STATISTICS]({ state, commit }) {
    LOGGER.groupCollapsed('PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_STATISTICS');
    try {
      const portfolios = state.portfolios;
      const portfolioBaseTokenToPriceMap = {};
      Object.values<Portfolio>(portfolios).forEach(portfolio => {
        portfolioBaseTokenToPriceMap[portfolio.baseTokenAddress] = BIG_ZERO;
      });
      const portfolioBaseTokens = Object.keys(portfolioBaseTokenToPriceMap);
      const lpTokenAddresses = Object.values<Portfolio>(portfolios).map(
        portfolio => portfolio.lpTokenAddress,
      );
      const { walletState } = useEVMWallet();
      const userAddress = walletState.account;

      const [prices, rawTotalSupply, rawPortfolioTokensOwned] = await Promise.all([
        fetchPricesInUSDByAddresses(portfolioBaseTokens),
        fetchTotalSupply(lpTokenAddresses),
        userAddress ? fetchPortfolioTokensOwned(lpTokenAddresses, userAddress) : null,
      ]);

      portfolioBaseTokens.forEach(
        (portfolioBaseTokenAddress, index) =>
          (portfolioBaseTokenToPriceMap[portfolioBaseTokenAddress] = toBigNumber(prices[index])),
      );

      Object.keys(portfolios).forEach((addr, index) => {
        const portfolio = portfolios[addr];

        // NOTE: total supply for cross chain portfolio will be set into `GET_PORTFOLIO_CROSS_CHAIN_INFO`.
        if (portfolio.type === PortfolioSource.PORTFOLIO_LOCALE) {
          portfolio.addTotalSupply(ethersToBigNumber(rawTotalSupply[index]));
        }
        if (userAddress) {
          portfolio.addBalanceOfWallet(ethersToBigNumber(rawPortfolioTokensOwned![index][0]));
        }
        portfolio.addPriceInUSD(portfolioBaseTokenToPriceMap[portfolios[addr].baseTokenAddress]);
      });
      commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, { isStatisticLoaded: true });
    } catch (e) {
      console.debug('[PORTFOLIO_ACTION_TYPES.GET_PORTFOLIO_STATISTICS:ERROR] Error: ', e);
    }
    LOGGER.groupEnd();
  },

  async [PORTFOLIO_ACTION_TYPES.PORTFOLIO_CHECK_ALLOWANCE](
    { dispatch },
    { token, amountInWei = BIG_ZERO, sendApprove = true, withDelegate = false },
  ): Promise<boolean> {
    try {
      if (token.isBaseToken() || token.isWETHToken()) {
        return false;
      }

      const { walletState } = useEVMWallet();
      const tokenContract = getErc20Contract(token.address, getInstance()?.web3.getSigner());
      const allowance = await tokenContract.allowance(
        walletState.account,
        withDelegate ? getLiquidityDelegateAddress() : getRouterAddress(),
      );
      const allowanceInWei = ethersToBigNumber(allowance);
      const needApprove = allowanceInWei.isZero() || allowanceInWei.lt(amountInWei);

      if (sendApprove && needApprove) {
        await dispatch(PORTFOLIO_ACTION_TYPES.PORTFOLIO_APPROVE, {
          token,
          amountInWei: toWei(fromWei(amountInWei, token.decimals), token.decimals).toFixed(0),
          withDelegate,
        });
      }

      return needApprove;
    } catch (e) {
      console.debug(e);
      throw e;
    }
  },
  async [PORTFOLIO_ACTION_TYPES.PORTFOLIO_APPROVE](
    _,
    { token, amountInWei, withDelegate = false },
  ) {
    try {
      if (token.isBaseToken() || token.isWETHToken()) {
        return;
      }

      const tokenContract = getErc20Contract(token.address, getInstance()?.web3.getSigner());

      const result = await transactionWithEstimatedGas(tokenContract, 'approve', [
        withDelegate ? getLiquidityDelegateAddress() : getRouterAddress(),
        amountInWei ? amountInWei : SOLIDITY_TYPE_MAXIMA[SolidityType.uint256].toString(),
      ]);

      await result.wait();
    } catch (e) {
      console.debug(e);
      throw e;
    }
  },
  async [PORTFOLIO_ACTION_TYPES.PORTFOLIO_ADD_LIQUIDITY](
    { state, dispatch, rootState },
    {
      selectedTokens,
      portfolio,
      lpAmountOut,
      destinationChainId,
      account,
      withDelegate,
    }: {
      selectedTokens: SelectedPortfolioTokens;
      portfolio: Portfolio;
      lpAmountOut: string;
      destinationChainId: ChainId;
      account: string;
      withDelegate: boolean;
    },
  ): Promise<ethers.providers.TransactionReceipt> {
    const { tokens, amountsIn, portfolioAddress } = getDepositMethodParameters(
      portfolio,
      selectedTokens,
    );

    const needApproveFlags = await Promise.all(
      tokens.map((token, index) =>
        dispatch(PORTFOLIO_ACTION_TYPES.PORTFOLIO_CHECK_ALLOWANCE, {
          token,
          amountInWei: amountsIn[index],
          sendApprove: false,
          withDelegate: withDelegate,
        }),
      ),
    );

    for (let i = 0; i < needApproveFlags.length; i++) {
      if (!needApproveFlags[i]) {
        continue;
      }

      await dispatch(PORTFOLIO_ACTION_TYPES.PORTFOLIO_APPROVE, {
        token: tokens[i],
        amountInWei: amountsIn[i],
        withDelegate: withDelegate,
      });
    }

    const minAmountOut = applySlippageInPercents(
      lpAmountOut,
      state.depositSettings.slippageTolerance,
    ).toFixed(0);

    const nativeTokenIndex = tokens.findIndex(token => token.isBaseToken());
    const msgValue = nativeTokenIndex === -1 ? 0 : amountsIn[nativeTokenIndex];

    const contract = withDelegate
      ? getLiquidityDelegateContract(getLiquidityDelegateAddress(), getInstance().web3.getSigner())
      : getRouterContract(getRouterAddress(), getInstance().web3.getSigner());
    // const routerContract = getRouterContract(getRouterAddress(), getInstance().web3.getSigner());

    const farm = rootState.farming.lp[portfolio.lpTokenAddress];

    const overrides = {
      value: msgValue,
    };

    console.log(
      withDelegate ? 'delegateDeposit' : 'addLiquidityToPortfolio',
      [
        tokens.map(token => token.address),
        amountsIn,
        minAmountOut,
        account,
        safeDateNowPlusEstimatedMinutes(state.depositSettings.transactionDeadline),
        portfolioAddress,
        withDelegate
          ? (farm as PortfolioFarm).farm
          : portfolio.type === PortfolioSource.PORTFOLIO_LOCALE
          ? 0
          : destinationChainId,
      ],
      overrides,
    );

    const result = await transactionWithEstimatedGas(
      contract,
      withDelegate ? 'delegateDeposit' : 'addLiquidityToPortfolio',
      [
        tokens.map(token => token.address),
        amountsIn,
        minAmountOut,
        account,
        safeDateNowPlusEstimatedMinutes(state.depositSettings.transactionDeadline),
        portfolioAddress,
        withDelegate
          ? (farm as PortfolioFarm).farm
          : portfolio.type === PortfolioSource.PORTFOLIO_LOCALE
          ? 0
          : destinationChainId,
      ],
      overrides,
    );

    return await result.wait();
  },
  // OLD UI
  async [PORTFOLIO_ACTION_TYPES.PORTFOLIO_WITHDRAW](
    { state, dispatch },
    {
      portfolio,
      tokenAmounts,
      account,
    }: {
      portfolio: Portfolio;
      tokenAmounts: string[];
      account: string;
    },
  ) {
    const { lptAmounts, tokenAddresses, portfolioAddress } = getWithdrawMethodParameters(portfolio);

    const minAmountsOut: string[] = tokenAmounts.map(amount =>
      applySlippageInPercents(amount, state.withdrawSettings.slippageTolerance).toFixed(0),
    );

    const lpToken: Token = new Token(
      portfolio.baseToken.chainId,
      portfolio.lpTokenAddress,
      18,
      'LPT',
    );
    await dispatch(PORTFOLIO_ACTION_TYPES.PORTFOLIO_CHECK_ALLOWANCE, {
      token: lpToken,
      amountInWei: BigNumber.max(...lptAmounts) ?? BIG_ZERO,
    });

    console.log('removeLiquidityFromPortfolio', [
      tokenAddresses,
      lptAmounts,
      minAmountsOut,
      account,
      safeDateNowPlusEstimatedMinutes(state.withdrawSettings.transactionDeadline),
      portfolioAddress,
    ]);

    const routerContract = getRouterContract(getRouterAddress(), getInstance().web3.getSigner());
    const result = await transactionWithEstimatedGas(
      routerContract,
      'removeLiquidityFromPortfolio(address[],uint256[],uint256[],address,uint256,address)',
      [
        tokenAddresses,
        lptAmounts,
        minAmountsOut,
        account,
        safeDateNowPlusEstimatedMinutes(state.withdrawSettings.transactionDeadline),
        portfolioAddress,
      ],
    );

    await result.wait();
  },
  async [PORTFOLIO_ACTION_TYPES.SET_DEPOSIT_SETTINGS]({ commit }, { settings }) {
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, { depositSettings: settings });
  },
  async [PORTFOLIO_ACTION_TYPES.SET_WITHDRAW_SETTINGS]({ commit }, { settings }) {
    commit(PORTFOLIO_MUTATION_TYPES.SET_STATE, { withdrawSettings: settings });
  },
};
const mutations = {
  [PORTFOLIO_MUTATION_TYPES.SET_STATE](_state, payload) {
    Object.keys(payload).forEach(key => {
      _state[key] = payload[key];
    });
  },
  [PORTFOLIO_MUTATION_TYPES.ADD_PORTFOLIO](_state, payload: Portfolio) {
    _state.portfolios[payload.contractAddress] = payload;
  },
};

// DEBUG

function isLoggingDisabled() {
  return !window['BLUESHIFT_DEBUG'].PORTFOLIOS;
}

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
